import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, Upload, CheckCircle, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FundFamilyApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    applicant_name: "",
    applicant_email: "",
    household_size: "",
    why_cant_afford: "",
    how_heard: "",
  });
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!form.applicant_name || !form.applicant_email || !form.household_size || !form.why_cant_afford) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.why_cant_afford.trim().length < 20) {
      toast.error("Please provide a more detailed answer about your situation");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("applicant_name", form.applicant_name);
      formData.append("applicant_email", form.applicant_email);
      formData.append("household_size", form.household_size);
      formData.append("why_cant_afford", form.why_cant_afford);
      formData.append("how_heard", form.how_heard);
      if (proofFile) {
        formData.append("proof_document", proofFile);
      }

      const res = await fetch(`${BACKEND_URL}/api/fund-family/apply`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Submission failed");
      }

      setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="card-dark p-8 max-w-md text-center" data-testid="application-submitted">
          <CheckCircle className="text-[#F7931A] mx-auto mb-4" size={48} />
          <h2 className="font-heading text-2xl font-bold text-white mb-2">Application Submitted</h2>
          <p className="text-zinc-400 text-sm mb-6">
            We'll review your application and get back to you by email. If approved, you'll receive full access to Bitcoin Brain — funded by a community member who believes in you.
          </p>
          <Button onClick={() => navigate("/")} className="btn-primary">
            Return Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="fund-family-apply-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">
            <Heart className="inline mr-2 text-[#F7931A]" size={20} />
            Fund a Family
          </h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl font-bold text-white mb-2">Apply for Funded Access</h2>
          <p className="text-zinc-400 text-sm">
            Community members have contributed to fund Bitcoin Brain access for families who need it.
            Fill out this form and we'll review your application.
          </p>
        </div>

        <div className="space-y-5" data-testid="application-form">
          {/* Name */}
          <div>
            <label className="text-zinc-400 text-xs block mb-1.5">Full Name *</label>
            <input type="text" value={form.applicant_name}
              onChange={e => setForm({ ...form, applicant_name: e.target.value })}
              placeholder="Your full name"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
              data-testid="apply-name-input" />
          </div>

          {/* Email */}
          <div>
            <label className="text-zinc-400 text-xs block mb-1.5">Email Address *</label>
            <input type="email" value={form.applicant_email}
              onChange={e => setForm({ ...form, applicant_email: e.target.value })}
              placeholder="your@email.com"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
              data-testid="apply-email-input" />
            <p className="text-zinc-600 text-xs mt-1">This is the email you'll use to access Bitcoin Brain if approved.</p>
          </div>

          {/* Household size */}
          <div>
            <label className="text-zinc-400 text-xs block mb-1.5">Household Size *</label>
            <select value={form.household_size}
              onChange={e => setForm({ ...form, household_size: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
              data-testid="apply-household-select">
              <option value="">Select household size</option>
              <option value="1">1 person</option>
              <option value="2">2 people</option>
              <option value="3-4">3-4 people</option>
              <option value="5-6">5-6 people</option>
              <option value="7+">7+ people</option>
            </select>
          </div>

          {/* Family story */}
          <div>
            <label className="text-zinc-400 text-xs block mb-1.5">Why is Bitcoin education important for your family? *</label>
            <textarea value={form.why_cant_afford}
              onChange={e => setForm({ ...form, why_cant_afford: e.target.value })}
              placeholder="Tell us about your family's situation and why this course matters to you. A few sentences is enough."
              rows={4}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none resize-none"
              data-testid="apply-reason-textarea" />
            <p className="text-zinc-600 text-xs mt-1">{form.why_cant_afford.length} characters — minimum 20 required</p>
          </div>

          {/* How heard */}
          <div>
            <label className="text-zinc-400 text-xs block mb-1.5">How did you hear about Bitcoin Brain?</label>
            <input type="text" value={form.how_heard}
              onChange={e => setForm({ ...form, how_heard: e.target.value })}
              placeholder="Optional — a friend, social media, school, etc."
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#F7931A]/50 focus:outline-none"
              data-testid="apply-howheard-input" />
          </div>

          {/* Proof document */}
          <div>
            <label className="text-zinc-400 text-xs block mb-1.5">
              Proof of Need (optional but strengthens your application)
            </label>
            <p className="text-zinc-600 text-xs mb-2">
              Upload a photo of any ONE: EBT/SNAP card, free/reduced lunch letter, Medicaid card, WIC document, or Section 8 voucher.
              Blur any numbers — we only need to see the document type and your name.
            </p>
            <label className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#F7931A]/40 transition-colors"
              data-testid="proof-upload-label">
              {proofFile ? (
                <>
                  <FileText className="text-[#F7931A]" size={20} />
                  <span className="text-zinc-300 text-sm">{proofFile.name}</span>
                </>
              ) : (
                <>
                  <Upload className="text-zinc-500" size={20} />
                  <span className="text-zinc-500 text-sm">Tap to upload a photo (max 5MB)</span>
                </>
              )}
              <input type="file" accept="image/*,.pdf" className="hidden"
                onChange={e => setProofFile(e.target.files[0])}
                data-testid="proof-file-input" />
            </label>
          </div>

          <Button onClick={handleSubmit} disabled={submitting}
            className="w-full btn-primary min-h-[48px]" data-testid="submit-application-btn">
            {submitting ? "Submitting..." : "Submit Application"}
          </Button>

          <p className="text-zinc-600 text-xs text-center">
            Your information is reviewed only by the Bitcoin Brain founder. It is never shared or sold.
          </p>
        </div>
      </main>
    </div>
  );
}
